'use client';

import { useEffect, useState, useRef } from 'react';
import { ImageIcon, Send, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/use-auth';
import { showWarningToast, showSuccessToast } from '@/components/ui/sonner';
import { useModalStore } from '@/store/modal.store';
import { buildDirectBackendUrl, createDirectBackendHeaders } from '@/lib/direct-backend-api';
import type { PublicationKind } from '@/features/comments/types/publication-kind';
import { getPublicationCopy } from '@/features/comments/utils/publication-copy.util';

interface CreateCommentProps {
  placeholder?: string;
  className?: string;
  onSubmit?: (comment: any) => void;
  articleId?: number;
  postId?: number;
  parentComment?: number;
  chapterId?: string;
  paragraphId?: string;
  publicationKind?: PublicationKind;
  focusOnMount?: boolean;
}

export function CreateComment({
  placeholder = 'Комментарий...',
  className = '',
  onSubmit,
  articleId,
  postId,
  parentComment,
  chapterId,
  paragraphId,
  publicationKind = 'comment',
  focusOnMount = false,
}: CreateCommentProps) {
  const copy = getPublicationCopy(publicationKind);
  const isReply = Boolean(parentComment);
  const [comment, setComment] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const loginButtonRef = useRef<HTMLButtonElement>(null);
  const { user, isAuthenticated, session } = useAuth();
  const { setIsOpenAuthDialog } = useModalStore();

  useEffect(() => {
    if (!focusOnMount || isReply) return;
    const node = isAuthenticated ? textareaRef.current : loginButtonRef.current;
    node?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    node?.focus();
  }, [focusOnMount, isAuthenticated, isReply]);

  const handleSubmit = async () => {
    console.log('handleSubmit called, comment:', comment);
    console.log('isAuthenticated:', isAuthenticated);
    console.log('articleId:', articleId, 'postId:', postId);

    // Проверяем авторизацию в первую очередь
    if (!isAuthenticated) {
      console.log('User not authenticated, opening auth dialog');
      setIsOpenAuthDialog(true);
      return;
    }

    if (!comment.trim()) return;

    // Проверяем, что указан хотя бы один родительский элемент
    if (!articleId && !postId && !parentComment) {
      console.log('No articleId, postId or parentComment provided');
      showWarningToast('Не указана статья или пост для комментария');
      return;
    }

    if (paragraphId && !chapterId) {
      showWarningToast('Некорректные параметры комментария к абзацу');
      return;
    }

    console.log('Starting comment submission...');
    setIsSubmitting(true);

    try {
      const commentData = parentComment
        ? {
            content: comment.trim(),
            parentComment,
          }
        : {
            content: comment.trim(),
            ...(articleId && { parentArticle: articleId }),
            ...(postId && { parentPost: postId }),
            ...(chapterId && { chapterId }),
            ...(paragraphId && { paragraphId }),
          };

      console.log('Sending comment data:', commentData);

      if (!session?.accessToken || !user?.id) {
        throw new Error('Требуется авторизация');
      }
      // authorId не отправляем — автор берётся из JWT на бэке
      const response = await fetch(buildDirectBackendUrl('/api/comments'), {
        method: 'POST',
        headers: createDirectBackendHeaders({
          accessToken: session.accessToken,
          hasJsonContentType: true,
        }),
        body: JSON.stringify(commentData),
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка создания комментария');
      }

      const newComment = await response.json();

      // Вызываем callback если передан
      if (onSubmit) {
        onSubmit(newComment);
      }

      // Очищаем форму
      setComment('');
      setImage(null);

      // Показываем успешное сообщение
      showSuccessToast(isReply ? copy.replyCreated : copy.created);
    } catch (error) {
      console.error('Error creating comment:', error);
      showWarningToast(
        error instanceof Error ? error.message : isReply ? copy.replyCreateError : copy.createError,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Проверяем авторизацию перед прикреплением изображения
    if (!isAuthenticated) {
      console.log('User not authenticated, opening auth dialog for image upload');
      setIsOpenAuthDialog(true);
      return;
    }

    const file = event.target.files?.[0];
    if (file) {
      setImage(file);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div
        className={`relative border-2 rounded-lg transition-colors duration-200 ${
          isFocused
            ? 'border-purple shadow-sm'
            : 'border-border dark:border-[#3a3f47] hover:border-input dark:hover:border-[#4a4f57]'
        }`}
      >
        <Textarea
          ref={textareaRef}
          id={publicationKind === 'review' && !isReply ? 'work-review-composer' : undefined}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          disabled={!isAuthenticated}
          placeholder={isAuthenticated ? placeholder : copy.guestPlaceholder}
          className={`min-h-[120px] resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-4 pr-20 ${
            isFocused
              ? 'border-blue-500 focus:border-blue-500'
              : 'border-border dark:border-[#3a3f47] hover:border-input dark:hover:border-[#4a4f57]'
          } bg-background dark:bg-[#3a3f47] text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-[#898989] disabled:opacity-50 disabled:cursor-not-allowed`}
        />

        <div className="absolute bottom-3 left-3 flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
          />
          {/* <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={!isAuthenticated}
            className="h-8 w-8 p-0 text-muted-foreground dark:text-[#898989] hover:text-foreground dark:hover:text-white hover:bg-muted dark:hover:bg-[#4a4f57] disabled:opacity-50 disabled:cursor-not-allowed"
            title={!isAuthenticated ? "Войдите в систему для прикрепления изображений" : "Прикрепить изображение"}
          >
            <ImageIcon className="h-4 w-4" />
          </Button> */}
          {image && (
            <span className="text-xs text-[#898989] bg-[#3a3f47] px-2 py-1 rounded">
              {image.name}
            </span>
          )}
        </div>

        <Button
          ref={loginButtonRef}
          type="button"
          id={publicationKind === 'review' && !isReply ? 'work-review-login' : undefined}
          onClick={handleSubmit}
          disabled={isAuthenticated ? !comment.trim() || isSubmitting : false}
          className="bg-[#A9F84A] hover:bg-[#8EE328] text-black font-medium px-6 rounded-lg absolute bottom-3 right-3 cursor-pointer"
          title={
            !isAuthenticated ? copy.guestActionTitle : isReply ? 'Отправить' : copy.submitTitle
          }
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Отправка...
            </>
          ) : !isAuthenticated ? (
            <>
              <Send className="h-4 w-4 mr-2" />
              Войти
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              {isReply ? 'Отправить' : copy.submitLabel}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
